import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Palette, PenLine, Settings, SlidersHorizontal } from "@/components/icons"
import { useGenerateImage } from "@/features/studio-ai/hooks/use-generate-image"
import { useSelectedImageModel } from "@/features/studio-ai/hooks/use-studio-image-models"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { useStudioAIStore } from "@/stores/studio-ai-store"

import { GenerationProgress } from "../generation-progress"
import { ImageAdvancedStep } from "./image-advanced-step"
import { ImageFormatStep } from "./image-format-step"
import { ImageQualityStep } from "./image-quality-step"
import { PromptStep } from "./prompt-step"
import { StyleStep } from "./style-step"
import { WizardContainer } from "./wizard-container"

type ImageWizardProps = {
  onComplete: (mediaType: MediaType) => void
  onCancel: () => void
}

export function ImageWizard({ onComplete, onCancel }: ImageWizardProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi" })
  const [currentStep, setCurrentStep] = useState(0)

  const wizardPrompt = useStudioAIStore((state) => state.wizardPrompt)
  const setWizardPrompt = useStudioAIStore((state) => state.setWizardPrompt)
  const imageSettings = useStudioAIStore((state) => state.imageSettings)
  const updateImageSettings = useStudioAIStore((state) => state.updateImageSettings)
  const selectedStyleTemplateId = useStudioAIStore((state) => state.selectedStyleTemplateId)
  const setSelectedStyleTemplateId = useStudioAIStore((state) => state.setSelectedStyleTemplateId)
  const selectedStyleAdherence = useStudioAIStore((state) => state.selectedStyleAdherence)
  const setSelectedStyleAdherence = useStudioAIStore((state) => state.setSelectedStyleAdherence)
  const generationState = useStudioAIStore((state) => state.generationState)
  const resetGenerationState = useStudioAIStore((state) => state.resetGenerationState)
  const imageReferenceUrl = useStudioAIStore((state) => state.imageReferenceUrl)
  const { mutateAsync: generateImage } = useGenerateImage()
  const { models, selectedModel, capabilities, showModelPicker, selectModel, requestModelId } =
    useSelectedImageModel()

  useEffect(() => {
    resetGenerationState()
  }, [resetGenerationState])

  const steps = useMemo(
    () => [
      { key: "prompt", label: t("wizard.steps.prompt"), icon: PenLine },
      { key: "format-quality", label: t("wizard.steps.formatQuality"), icon: SlidersHorizontal },
      { key: "style", label: t("wizard.steps.style"), icon: Palette },
      { key: "advanced", label: t("wizard.steps.advanced"), icon: Settings },
    ],
    [t]
  )

  const isNextDisabled = useMemo(() => {
    if (currentStep === 0) return wizardPrompt.trim().length === 0
    return false
  }, [currentStep, wizardPrompt])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step)
    }
  }

  const handleGenerate = async () => {
    try {
      await generateImage({
        prompt: wizardPrompt,
        size: imageSettings.size,
        // A persisted quality the chosen model doesn't offer would be coerced
        // to `medium` server-side — send the model's default instead so the
        // result matches the card the user sees selected.
        quality: capabilities.qualities.includes(imageSettings.quality)
          ? imageSettings.quality
          : capabilities.defaultQuality,
        style: imageSettings.style,
        // Streaming pipeline produces 1 image per call; clamp here in case
        // a stale persisted setting (or future caller) tries to send n > 1.
        n: 1,
        compression: imageSettings.compression,
        output_format: imageSettings.outputFormat,
        // The control is hidden for models without an alpha channel; clamp the
        // stored value too so a pre-existing "transparent" can't leak out.
        background: capabilities.supportsTransparent ? imageSettings.background : "opaque",
        model_id: requestModelId,
        style_template_id: selectedStyleTemplateId ?? undefined,
        style_adherence: selectedStyleTemplateId ? selectedStyleAdherence : undefined,
        reference_image_url: imageReferenceUrl ?? undefined,
      })
      onComplete(MediaType.Image)
    } catch (error) {
      const message = error instanceof Error ? error.message : ""
      if (message.toLowerCase().includes("style template not found or access denied")) {
        toast.error(t("image.style.templateUnavailable"))
      }
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <PromptStep
            mediaType={MediaType.Image}
            prompt={wizardPrompt}
            size={imageSettings.size}
            style={imageSettings.style}
            onPromptChange={setWizardPrompt}
          />
        )
      case 1:
        return (
          <div className="mx-auto max-w-2xl space-y-8">
            <ImageFormatStep
              selected={imageSettings.size}
              onSelect={(size) => updateImageSettings({ size })}
            />
            <ImageQualityStep
              models={models}
              qualities={capabilities.qualities}
              selected={imageSettings.quality}
              selectedModelId={selectedModel?.id}
              showModelPicker={showModelPicker}
              onSelect={(quality) => updateImageSettings({ quality })}
              onSelectModel={selectModel}
            />
          </div>
        )
      case 2:
        return (
          <StyleStep
            mediaType={MediaType.Image}
            selected={imageSettings.style}
            selectedAdherence={selectedStyleAdherence}
            selectedTemplateId={selectedStyleTemplateId}
            onAdherenceSelect={setSelectedStyleAdherence}
            onSelect={(style) => updateImageSettings({ style })}
            onTemplateSelect={(templateId: string | null) => {
              setSelectedStyleTemplateId(templateId)
            }}
          />
        )
      case 3:
        return (
          <ImageAdvancedStep
            settings={imageSettings}
            supportsTransparent={capabilities.supportsTransparent}
            onSettingsChange={updateImageSettings}
          />
        )
      default:
        return null
    }
  }

  return (
    <WizardContainer
      currentStep={currentStep}
      isGenerating={generationState.isGenerating}
      isNextDisabled={isNextDisabled}
      steps={steps}
      title={t("image.title")}
      onBack={handleBack}
      onGenerate={() => void handleGenerate()}
      onNext={handleNext}
      onStepClick={handleStepClick}
      onCancel={() => {
        resetGenerationState()
        onCancel()
      }}
    >
      {/* Live progress card with the partial image preview while generation is in flight. */}
      {(generationState.isGenerating || generationState.partialImageUrl) && (
        <div className="mb-4">
          <GenerationProgress jobId={generationState.currentJobId ?? undefined} />
        </div>
      )}
      {renderStep()}
    </WizardContainer>
  )
}
